/**
 * SVD-native price / promo resolution — peer of `svd-cascade-helper`, for the SVD buy-box flow only.
 *
 * Mirrors the outputs of the legacy `productHelper.getProductPriceDetails` + `festivalOffersUtility`, but
 * resolves the variant SVD-natively (`svdCascadeHelper.resolveSelectedVariant`) and reads promo off the
 * resolved variant's `promoConfig`. The time-window filter `getValidPromo` is selection-agnostic (it just
 * filters a `PromoConfig[]` by start/end time), so it is reused directly rather than duplicated — the SVD
 * flow only supplies the variant. (Per-serving numerics are surfaced onto the variant's legacy fields by
 * the V2 adapter, so the existing per-serving display reads them unchanged.)
 *
 * Never reads a legacy `ProductOption` / `selectedOptions`; the two flows stay fully separate (see
 * `pdp-purchase-widget-solutioning.md`).
 */

import { getValidPromo } from '../utils/festivalOffersUtility'
import formatDisplayAmountValue from '../utils/formatDisplayAmountValue'

import { getSvdVariantsForOptions, resolveSelectedVariant } from './svd-cascade-helper'

import type Product from '../domains/Product'
import type { SvdSelection } from '../domains/svd/svd-product.types'

/** Legacy-parity savings %: floor((trad − sale) / trad × 100), only when trad > sale (else undefined). */
function getSvdSavePercentage(traditionalPrice: number | undefined, salePrice: number): number | undefined {
  if (!traditionalPrice || traditionalPrice <= salePrice) return undefined
  return Math.floor(((traditionalPrice - salePrice) / traditionalPrice) * 100) || undefined
}

/** The valid (in-window) promo of the SVD-resolved variant, or null. */
function resolvedPromo(product: Product, selected: SvdSelection[], serverVariantId?: number) {
  const variant = resolveSelectedVariant(product, selected, serverVariantId)
  return getValidPromo(variant?.promoConfig)
}

/**
 * Full SVD price details — structural match of `productHelper.getProductPriceDetails`'s return so the
 * wrapper's `isSvd ? getSvdPriceDetails() : getProductPriceDetails()` union stays type-consistent.
 * salePrice = resolved variant price; traditional = `traditionalRetailPrice`; festival / subscription-with-
 * offer prices come from the valid promo. Subscription-festival saving % mirrors legacy: saving of the
 * subscription price vs the sale price, only when both a festival and subscription offer exist.
 */
export function getSvdPriceDetails(product: Product, selected: SvdSelection[], serverVariantId?: number) {
  const variant = resolveSelectedVariant(product, selected, serverVariantId)
  const salePrice = variant?.price ?? 0
  const traditionalPrice = variant?.traditionalRetailPrice
  const savingPercentage = getSvdSavePercentage(traditionalPrice, salePrice)

  const validPromo = getValidPromo(variant?.promoConfig)
  // Treat a falsy (0 / absent) offer price as "no offer" — legacy parity (`... || undefined`).
  const festivalOfferPrice = validPromo?.promoPrice ? validPromo.promoPrice : undefined
  const subscriptionPrice = validPromo?.subscriptionPriceWithOffer ? validPromo.subscriptionPriceWithOffer : undefined
  const subscriptionFestivalSavingPercentage =
    subscriptionPrice && festivalOfferPrice ? getSvdSavePercentage(salePrice, subscriptionPrice) : undefined

  return {
    salePrice,
    festivalOfferPrice,
    formattedFestivalOfferPrice: festivalOfferPrice ? formatDisplayAmountValue(festivalOfferPrice) : undefined,
    formattedSalePrice: formatDisplayAmountValue(salePrice),
    formattedTraditionalPrice: traditionalPrice ? formatDisplayAmountValue(traditionalPrice) : undefined,
    formattedSubscriptionPrice: subscriptionPrice ? formatDisplayAmountValue(subscriptionPrice) : undefined,
    savingPercentage,
    traditionalPrice,
    subscriptionPrice,
    subscriptionFestivalSavingPercentage,
  }
}

/** Festival per-unit offer price of the resolved variant's valid promo (parity: `getFestivalOfferPricePerUnit`). */
export function getSvdFestivalOfferPricePerUnit(
  product: Product,
  selected: SvdSelection[],
  serverVariantId?: number,
): number | undefined {
  return resolvedPromo(product, selected, serverVariantId)?.promoPricePerUnit ?? undefined
}

/** Subscription-with-offer per-unit price of the resolved variant's valid promo. */
export function getSvdSubscriptionOfferPricePerUnit(
  product: Product,
  selected: SvdSelection[],
  serverVariantId?: number,
): number | undefined {
  return resolvedPromo(product, selected, serverVariantId)?.subscriptionPriceWithOfferPerUnit ?? undefined
}

/** Store-credit grant of the resolved variant's valid promo (parity: `getStoreCreditGrant`; 0 when none). */
export function getSvdStoreCreditGrant(product: Product, selected: SvdSelection[], serverVariantId?: number): number {
  return resolvedPromo(product, selected, serverVariantId)?.storeCreditGrant ?? 0
}

/** Whether the resolved variant's valid promo marks it final-sale (parity: `getIsFinalSaleProduct`). */
export function getSvdIsFinalSale(product: Product, selected: SvdSelection[], serverVariantId?: number): boolean {
  return Boolean(resolvedPromo(product, selected, serverVariantId)?.isFinalSale)
}

/**
 * True when EVERY ACTIVE variant carrying the given VDA value is on a valid (in-window) promo — the SVD-native
 * analog of `areAllColorVariantsOnSale`, for the on-sale red-dot. `getValidPromo` is time-based (checks the
 * current promo window), so this is the CLIENT recompute used after mount; the server's precomputed
 * `VdaValue.showRedDot` is honoured for the first (SSR) render to keep hydration stable. Primary-swatch only
 * (the caller gates). Returns false when no variant carries the value.
 */
export function getSvdAllVariantsOnSaleForValue(product: Product, vdaKey: string, valueKey: string): boolean {
  const variants = getSvdVariantsForOptions(product, [{ vdaKey, valueKey }])
  if (variants.length === 0) return false
  return variants.every(variant => Boolean(getValidPromo(variant.promoConfig)?.promoPrice))
}
